import React, { useContext, useEffect, useReducer, useState } from 'react';
import {
  AddressDetails,
  DirectDebitDetails,
  DocumentTreeItem,
  PersonalDetailType,
  PolicyResponse,
  PolicyServer,
  ProductType,
  ProductVariant,
  VehicleState,
  NavigationStep,
  JourneyState,
  JourneyContext,
  QuoteProduct,
  RegisterPolicyDto
} from '../../common/CommonTypes';
import NavBar from '../../common/NavBar';
import CustomerNavStepper from '../customer/CustomerNavStepper';
import {
  PageAbsolutePath,
  RegistrationState,
  initialJourneyState,
  functionNotImplemented,
  VehicleIdType,
  PageRelativePath
} from '../../../consts/Common';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useCurrentPath } from '../useCurrentPath';
import {
  filterProductDocuments,
  getSelectedProducts,
  getSelectedVariant
} from '../../common/ProductMethods';
import { getPolicyServer } from '../PolicyMethods';
import MobileInfoBar from '../../common/MobileInfoBar';
import PageInfo from '../PageInfo';
import { PolicyApi } from '../../../api/PolicyApi';
import JourneyReducer from '../../../journeyState/JourneyReducer';
import ReloadHandler from '../../common/ReloadHandler';
import { GetNavDispatch } from '../../common/NavStepper';
import { SiteContext } from '../../../context/SiteContext';
import { CommonValidation } from '../../common/CommonValidation/commonValidation';
import { useTranslation } from 'react-i18next';

const CustomerJourneyManager = (): JSX.Element => {
  const { state } = useLocation();
  const { site } = useContext(SiteContext);
  const { t } = useTranslation();
  const CustomerPageOrder: NavigationStep[] = [
    {
      page: PageAbsolutePath.Customer.VehicleDetails,
      pageIndex: 0,
      pageName: t('breadcrumb.vehicle-details')
    },
    {
      page: PageAbsolutePath.Customer.Products,
      pageIndex: 1,
      pageName: t('breadcrumb.products')
    },
    {
      page: PageAbsolutePath.Customer.CreatePlan,
      pageIndex: 1,
      pageName: t('breadcrumb.products')
    },
    {
      page: PageAbsolutePath.Customer.PersonalDetails,
      pageIndex: 2,
      pageName: t('breadcrumb.personal-details')
    },
    {
      page: PageAbsolutePath.Customer.Summary,
      pageIndex: 3,
      pageName: t('breadcrumb.checkout')
    },
    {
      page: PageAbsolutePath.Customer.SinglePayment,
      pageIndex: 3,
      pageName: t('breadcrumb.checkout')
    },
    {
      page: PageAbsolutePath.Customer.DirectDebit,
      pageIndex: 3,
      pageName: t('breadcrumb.checkout')
    },
    {
      page: PageAbsolutePath.Customer.Confirmation,
      pageIndex: 4,
      pageName: t('breadcrumb.confirmation')
    }
  ];
  const pageOrder: NavigationStep[] = CustomerPageOrder;

  const initialCustomerJourneyState: JourneyState = { ...initialJourneyState };

  if (state) {
    const { quote, vehicleIdType } = state;
    if (quote) {
      initialCustomerJourneyState.isOpensiteUser = false;
      initialCustomerJourneyState.vehicle = quote.vehicleInfo;
      if (quote.products && quote.products.products) {
        initialCustomerJourneyState.products = quote.products.products;
      }
      initialCustomerJourneyState.quoteProducts = quote.quotedProducts;
      initialCustomerJourneyState.products.forEach((product) => {
        const quoteProductVariantId =
          initialCustomerJourneyState.quoteProducts.find(
            (qp) => qp.productTypeId === product.productTypeId
          )?.variantId;
        if (quoteProductVariantId) {
          const productVariant = product.productVariants.find(
            (pv) => pv.variantId === quoteProductVariantId
          );
          if (productVariant) {
            productVariant.isSelected = true;
          }
        }
      });
      initialCustomerJourneyState.personalDetail = quote.personalDetail;
      initialCustomerJourneyState.token = quote.token;
      if (quote.products && quote.products.documents) {
        initialCustomerJourneyState.documents = quote.products.documents;
      }
      initialCustomerJourneyState.hasQuoteExpired = quote.hasExpired;
      initialCustomerJourneyState.dealerCode = quote.dealerCode;
    } else if (
      vehicleIdType === VehicleIdType.Vrn ||
      vehicleIdType === VehicleIdType.Vin
    ) {
      initialCustomerJourneyState.isOpensiteUser = true;
      initialCustomerJourneyState.dealerCode = site.brand.openSiteDealerCode;
      initialCustomerJourneyState.token = site.user.token;
    }
  }

  const [journey, dispatch] = useReducer(
    JourneyReducer,
    initialCustomerJourneyState
  );

  const navigate = useNavigate();
  const currentPath = useCurrentPath();
  const [updateDocuments, setUpdateDocuments] = useState(false);
  const {
    products,
    personalDetail,
    vehicle,
    directDebitDetails,
    navigationStatus,
    registrationFailMsg,
    dealerCode,
    renewalMarketing
  } = journey;

  const token = journey.token || site.user.token;

  useEffect(() => {
    if (CommonValidation.isNotEmpty(registrationFailMsg)) {
      navigate(PageRelativePath.CustomerFailedRegistration, {
        relative: 'path'
      });
    }
  }, [registrationFailMsg]);

  useEffect(() => {
    setProductDocuments(
      journey.products,
      journey.vehicle.vehicleGapIntroductionDate,
      journey.documents,
      currentPath
    );
  }, [currentPath]);

  useEffect(() => {
    if (updateDocuments) {
      setProductDocuments(
        journey.products,
        journey.vehicle.vehicleGapIntroductionDate,
        journey.documents,
        currentPath
      );
    }
  }, [updateDocuments]);

  const setProductDocuments = (
    initialProducts: ProductType[],
    gapIntroductionDate: string | undefined,
    documents: DocumentTreeItem[],
    path: string
  ): void => {
    setUpdateDocuments(false);

    const newDocuments = filterProductDocuments(
      initialProducts,
      gapIntroductionDate,
      documents,
      path
    );

    if (newDocuments) {
      onUpdateProductDocuments(newDocuments);
    }
  };

  const setExpandedDocIds = (docIds: string[]) => {
    dispatch({
      type: 'set-expanded-node-ids',
      expandedDocIds: docIds
    });
  };

  const onUpdateVehicleDetails = (vehicleState: VehicleState) => {
    dispatch({
      type: 'set-vehicle',
      vehicle: vehicleState
    });
  };

  const onChangeMileage = (currentMileage: number | null) => {
    dispatch({
      type: 'set-current-mileage',
      currentMileage: currentMileage
    });
  };

  const onChangeVehiclePurchasePrice = (
    e: React.FormEvent<HTMLInputElement>
  ) => {
    const value = (e.target as HTMLInputElement).value;

    if (CommonValidation.isEmpty(value) || isNaN(parseInt(value))) {
      dispatch({
        type: 'set-vehicle-purchase-price',
        vehiclePurchasePrice: 0
      });
    } else {
      dispatch({
        type: 'set-vehicle-purchase-price',
        vehiclePurchasePrice: parseInt(value)
      });
    }
  };

  const onUpdateVehicleVrn = (vrn: string) => {
    dispatch({
      type: 'set-vehicle-vrn',
      vrn: vrn
    });
  };

  const onChangeVehicleDeliveryDate = (vehicleDeliveryDate: string) => {
    dispatch({
      type: 'set-vehicle-delivery-date',
      vehicleDeliveryDate: vehicleDeliveryDate
    });
  };

  const onChangeVehicleGapIntroductionDate = (
    e: React.FormEvent<HTMLInputElement>
  ) => {
    dispatch({
      type: 'set-vehicle-gap-introduction-date',
      vehicleGapIntroductionDate: (e.target as HTMLInputElement).value
    });
  };

  const onChangeSalesId = (salesId: string) => {
    dispatch({
      type: 'set-sales-id',
      salesId: salesId
    });
  };

  const onUpdateProducts = (
    initialProducts: ProductType[],
    updateProductDocuments?: boolean
  ) => {
    dispatch({
      type: 'set-products',
      products: initialProducts
    });
    if (updateProductDocuments === true) {
      setUpdateDocuments(true);
    }
  };

  const onUpdateQuoteProducts = (quoteProducts: QuoteProduct[]) => {
    dispatch({
      type: 'set-quote-products',
      quoteProducts
    });
  };

  const onUpdateProductDocuments = (documents: DocumentTreeItem[]) => {
    dispatch({
      type: 'set-documents',
      documents
    });
  };

  const mapVariants = (
    variants: ProductVariant[],
    selectedVariantId: string
  ): ProductVariant[] => {
    const updatedVariants = variants.map((v) => ({
      ...v,
      isSelected: v.variantId === selectedVariantId
    }));

    return updatedVariants;
  };

  const onUpdateProductVariant = (productId: number, variantId: string) => {
    const updatedProducts = journey.products.map((p) =>
      p.productTypeId === productId
        ? { ...p, productVariants: mapVariants(p.productVariants, variantId) }
        : { ...p }
    );

    dispatch({
      type: 'set-products',
      products: updatedProducts
    });
  };

  const setPersonalDetails = (personalDetailType: PersonalDetailType) => {
    dispatch({
      type: 'set-personal-details',
      personalDetail: personalDetailType
    });
  };

  const setAddressSinglePayment = (addressDetails: AddressDetails) => {
    dispatch({
      type: 'set-address-single-payment',
      addressDetails: addressDetails
    });
  };

  const setDirectDebitDetails = (directDebit: DirectDebitDetails) => {
    dispatch({
      type: 'set-direct-debit-details',
      directDebitDetails: directDebit
    });
  };

  const onUpdateAgreedTermsAndConditions = (hasAgreed: boolean) => {
    dispatch({
      type: 'set-agreed-terms-and-conditions',
      hasAgreed
    });
  };

  const onUpdateFoundExistingQuotes = (foundExistingQuotes: boolean) => {
    dispatch({
      type: 'set-included-quotes-data',
      foundExistingQuotes
    });
  };

  const onUpdateRenewalMarketing = (consentFlag: boolean) => {
    dispatch({
      type: 'set-consent-to-renewal-marketing',
      renewalMarketing: consentFlag
    });
  };

  const onSetJourney = (journeyState: JourneyState) => {
    dispatch({
      type: 'set-journey-state',
      journeyState
    });
  };

  const register = (cardTransactionId: string) => {
    const controller = new AbortController();

    const selectedProducts = getSelectedProducts(products);

    const uniquePaymentMethodSelected: string[] = [];

    selectedProducts.forEach((p) => {
      const selectedVariant = getSelectedVariant(p.productVariants);
      const paymentMethod = selectedVariant?.paymentMethod;

      if (
        paymentMethod &&
        !uniquePaymentMethodSelected.find((pm) => pm == paymentMethod)
      ) {
        uniquePaymentMethodSelected.push(paymentMethod);
      }
    });

    const policy: PolicyServer[] = [];

    uniquePaymentMethodSelected.forEach((pm) => {
      const policyDetail = getPolicy(pm, cardTransactionId);

      if (policyDetail) {
        policy.push(policyDetail);
      }
    });

    const successCallback = (policyResponse: PolicyResponse[]) => {
      const orderNumbers = policyResponse.map((pr) => pr.policyNumber);

      dispatch({
        type: 'set-order-numbers',
        orderNumbers: orderNumbers,
        authorisationCode: ''
      });
    };

    const failureCallback = () => {
      if (cardTransactionId) {
        setRegistrationState(RegistrationState.VoidingPayment);
      } else {
        setRegistrationFailMsg(t('common-error-msg.submission-failed-message'));
      }
    };

    const registerPolicyDto: RegisterPolicyDto = {
      policy: policy,
      allowPastPurchaseDate: false
    };

    setRegistrationState(RegistrationState.Registering);

    new PolicyApi().register(
      registerPolicyDto,
      token,
      successCallback,
      failureCallback,
      controller.signal
    );

    return () => {
      controller.abort();
    };
  };

  const getPolicy = (
    paymentMethod: string,
    cardTransactionId: string
  ): PolicyServer | undefined => {
    const policyStartDate = '';
    const isLateRenewal = false;

    return getPolicyServer(
      paymentMethod,
      cardTransactionId,
      products,
      vehicle,
      personalDetail,
      directDebitDetails,
      policyStartDate,
      isLateRenewal,
      dealerCode,
      '',
      vehicle.dealerReference,
      site.brand.worldPay,
      undefined,
      renewalMarketing
    );
  };

  const onPaymentVoided = () => {
    dispatch({
      type: 'set-registration-fail-msg',
      message: t('common-error-msg.payment-voided-message')
    });
  };

  const onPaymentFailure = () => {
    dispatch({
      type: 'set-registration-fail-msg',
      message: 'Failed to make the payment.'
    });
  };

  const setRegistrationFailMsg = (message: string) => {
    dispatch({
      type: 'set-registration-fail-msg',
      message: message
    });
  };

  const setQuoteId = (quoteId: number) => {
    dispatch({
      type: 'set-quote-id',
      quoteId: quoteId
    });
  };

  const setHasQuoteExpired = (hasQuoteExpired: boolean) => {
    dispatch({
      type: 'set-quote-expired',
      hasQuoteExpired: hasQuoteExpired
    });
  };

  const setIsExpiredMessageAcknowledged = (
    isExpiredMessageAcknowledged: boolean
  ) => {
    dispatch({
      type: 'set-message-acknowledged',
      isExpiredMessageAcknowledged: isExpiredMessageAcknowledged
    });
  };

  const setRegistrationState = (regState: number) => {
    dispatch({
      type: 'set-registration-state',
      regState: regState
    });
  };

  const setExceedsUpsellMileageLimit = (exceedsUpsellMileageLimit: boolean) => {
    dispatch({
      type: 'set-exceeds-upsell-mileage-limit',
      exceedsUpsellMileageLimit: exceedsUpsellMileageLimit
    });
  };

  const onPageChange = (page: string, status: boolean) => {
    const action = GetNavDispatch(page, status);

    if (action !== undefined) {
      dispatch(action);
    }
  };

  const customerJourneyContext: JourneyContext = {
    journey: journey,
    onUpdateVehicleDetails: onUpdateVehicleDetails,
    onChangeMileage: onChangeMileage,
    onChangeVehiclePurchasePrice: onChangeVehiclePurchasePrice,
    onChangeVehicleDeliveryDate: onChangeVehicleDeliveryDate,
    onChangeVehicleGapIntroductionDate: onChangeVehicleGapIntroductionDate,
    onChangeSalesId: onChangeSalesId,
    onUpdateProducts: onUpdateProducts,
    onUpdateQuotedProducts: onUpdateQuoteProducts,
    onUpdateProductDocuments: onUpdateProductDocuments,
    onUpdateProductVariant: onUpdateProductVariant,
    setPersonalDetails: setPersonalDetails,
    setAddressSinglePayment: setAddressSinglePayment,
    setDirectDebitDetails: setDirectDebitDetails,
    onUpdateAgreedTermsAndConditions: onUpdateAgreedTermsAndConditions,
    onRegister: register,
    onPaymentVoided: onPaymentVoided,
    onPaymentFailure: onPaymentFailure,
    onPageChange: onPageChange,
    getPolicy: getPolicy,
    setRegistrationFailMsg: setRegistrationFailMsg,
    setExpandedDocIds: setExpandedDocIds,
    onUpdateFoundExistingQuotes: onUpdateFoundExistingQuotes,
    onPolicyStartDateChange: functionNotImplemented,
    setQuoteId: setQuoteId,
    setHasQuoteExpired: setHasQuoteExpired,
    onUpdateVehicleVrn: onUpdateVehicleVrn,
    setIsExpiredMessageAcknowledged: setIsExpiredMessageAcknowledged,
    setExceedsUpsellMileageLimit: setExceedsUpsellMileageLimit,
    onUpdateRenewalMarketing: onUpdateRenewalMarketing,
    onSetJourney: onSetJourney
  };

  const isAllowedToRenderMIB = () => {
    const notAllowed: string[] = [
      PageAbsolutePath.Customer.Confirmation,
      PageAbsolutePath.FailedRegistration
    ];
    return !notAllowed.includes(currentPath);
  };

  return (
    <>
      <div className='flex-column main-column'>
        <div className='main-content'>
          <NavBar />
          <CustomerNavStepper
            products={products}
            pageOrder={pageOrder}
            navigationStatus={navigationStatus}
            currentPath={currentPath}
          />
          <div className='page-container'>
            <div className='page-header-line' />
            <Outlet context={customerJourneyContext} />
            {isAllowedToRenderMIB() && (
              <MobileInfoBar
                products={journey.products}
                documents={journey.documents}
                expandedDocIds={journey.expandedDocIds}
                currentPath={currentPath}
                setExpandedDocIds={setExpandedDocIds}
              />
            )}
          </div>
        </div>
      </div>
      <div className='flex-column desktop-column'>
        <PageInfo
          products={journey.products}
          documents={journey.documents}
          expandedDocIds={journey.expandedDocIds}
          currentPath={currentPath}
          setExpandedDocIds={setExpandedDocIds}
        />
      </div>
      <ReloadHandler reloadPath={PageAbsolutePath.Customer.Landing} />
    </>
  );
};

export default CustomerJourneyManager;
